import { useEffect, type RefObject } from 'react';

/**
 * Custom property the layout reads to reserve room under the sticky header.
 * Consumed by `.main-content-wrapper` in `packages/themes/default-theme/Common/global.scss`.
 */
export const PRIMIS_HEIGHT_VAR = '--primis-video-height';

// The only stable part of the vendor's injected markup: the player element's id is
// `primis_playerSekindoSPlayer<hash>`, nested inside its own `primis_container_div_<hash>`.
// The slot's own `adm-ad-rendered` / `adm-ad-empty` classes are NOT usable as the signal —
// `adm-ad-rendered` stays on the slot after the viewer closes the player, so keying off it
// would reserve the band forever once an ad had been shown.
const PLAYER_ID_PREFIX = 'primis_player';

function measurePlayer(slot: HTMLElement): number {
  const player = slot.querySelector<HTMLElement>(`[id^="${PLAYER_ID_PREFIX}"]`);
  return player ? player.getBoundingClientRect().height : 0;
}

/**
 * Publishes the live height of the Primis video overlay as a CSS custom property, so the
 * layout can reserve exactly as much room as the player actually occupies — and none when
 * there is no player.
 *
 * The vendor renders the player as a `position: fixed` overlay pinned directly under the
 * sticky header. Being fixed, it contributes no layout height of its own, so without this the
 * player would cover the top of the first content block. Equally, reserving the space
 * unconditionally would reinstate the empty band under the header that we deliberately trimmed
 * — and the slot is empty for most viewers (unfilled inventory, or the player closed via its
 * X button).
 *
 * A `MutationObserver` covers all three transitions with one mechanism: the player being
 * injected (childList), resized by the vendor (its inline `style`), and removed when closed.
 */
export function usePrimisVideoSpace(slotRef: RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    // The slot div renders unconditionally, so the ref is attached before this effect runs.
    const slot = slotRef.current!;
    const root = document.documentElement;

    const sync = () => {
      const height = measurePlayer(slot);
      if (height > 0) {
        root.style.setProperty(PRIMIS_HEIGHT_VAR, `${height}px`);
      } else {
        root.style.removeProperty(PRIMIS_HEIGHT_VAR);
      }
    };

    sync();
    const observer = new MutationObserver(sync);
    observer.observe(slot, { childList: true, subtree: true, attributes: true, attributeFilter: ['style'] });

    return () => {
      observer.disconnect();
      root.style.removeProperty(PRIMIS_HEIGHT_VAR);
    };
  }, [slotRef]);
}
